/* eslint-disable */
import * as React from "react";
import {observer} from "mobx-react";
import {autorun, configure} from "mobx"; configure({ enforceActions: "never" });

import "./app.scss";

import {AppConfiguration} from "../../core/config/app-configuration";
import {ModuleConfiguration} from "../../core/config/module-configuration";
import {MapViewerConfiguration} from "../../config/map-viewer-configuration";

import {ErrorBoundary} from "../../core/components/error-boundary/error-boundary";
import esriConfig from "@arcgis/core/config";

import {MapState} from "../../core/map-state";
import {UiState} from "../../core/ui-state";
import {LayerStack} from "./map/layer-stack/layer-stack";
import {AppState, MessageBoxType} from "./app-mobx";

import {Header} from "./header/header";
import {Footer} from "./footer/footer";
import {Navigation} from "./navigation/navigation";
import {MobileNavigation} from "./navigation/mobile-navigation";
import {Breadcrumbs} from "./navigation/breadcrumbs";
import {AuftragsverwaltungFooter} from "./footer/auftragsverwaltung-footer";
import {Maintenance} from "../../core/components/maintenance/maintenance";

import {GeoProxy} from "../../core/geo-proxy";
import {Item, Message, Ref, Sidebar as SemanticSidebar} from "semantic-ui-react";

import {TopicConfiguration} from "../../config/topic-configuration";
import {MessageBox} from "../../core/components/message-box/message-box";
import MessageBlock from "../../core/components/message-block/message-block";
import {Popup} from "../../core/components/popup/popup";

import {BasemapGallery} from "../../core/components/basemap-gallery/basemap-gallery";

import {NavigateFunction} from "react-router";
import CookieBanner from "../../core/components/cookie-banner/cookie-banner";
import EsriCompass from "@arcgis/core/widgets/Compass";
import EsriLocate from "@arcgis/core/widgets/Locate";
import EsriGeometry from "@arcgis/core/geometry/Geometry";
import {AuftragsverwaltungRouter} from "../../pages/auftragsverwaltung-router";
import {Kontakt} from "../../pages/kontakt";
import {Faq} from "../../pages/faq";
import {Ueber} from "../../pages/ueber";
import {Aside} from "./aside/aside";
import {Auftragsverwaltung} from "../../stores/auftragsverwaltung-mobx";
import {Search} from "../../core/components/search/search";
import {NavigationConfiguration} from "../../config/navigation-configuration";
import {Authentication} from "../../core/authentication";
import {Icon} from "../../core/components/icon/icon";
import {MapButtons} from "./map-buttons/map-buttons";
import {Municipality} from "../../models/municipality";
import {Project} from "../../models/project";
import {Contractor} from "../../models/contractor";

/* Lazy load Webmap to show loader while importing */
const WebMap = React.lazy(() =>
    import("../../core/geo").then((module) => ({ default: module.WebMap }))
);

export interface AppProps {
    config: AppConfiguration;
    moduleConfig: ModuleConfiguration;
    appStore: AppState;
    location: Location;
    navigate: NavigateFunction;
    params: any;
    searchParams: URLSearchParams;
    setSearchParams: () => void;
}

/**
 * Anwendungsklasse
 */
@observer
export class App extends React.Component<AppProps, any> {
    uiStore: UiState;
    mapStore: MapState;
    auftragsverwaltung: Auftragsverwaltung;

    mapViewerConfiguration: MapViewerConfiguration;
    appConfig: AppConfiguration;

    appUiRef = React.createRef<HTMLDivElement>();

    zoom?: number; // Zoom quick-fix

    constructor(props: AppProps) {
        super(props);
        this.uiStore = new UiState();
        this.mapStore = new MapState();
        this.state = {
            northFacing: true
        };

        this.uiStore.setUiSize(window.innerWidth, window.innerHeight);

        this.mapViewerConfiguration = this.props.moduleConfig as MapViewerConfiguration;
        this.appConfig = props.config as AppConfiguration;
        this.zoom = this.appConfig.mapView.zoom; // Zoom quick-fix

        this.auftragsverwaltung = new Auftragsverwaltung({
            config: this.appConfig,
            mapViewerConfig: this.mapViewerConfiguration,
            mapStore: this.mapStore
        });

        // configure proxy page
        const geoProxy = new GeoProxy(this.appConfig.proxy);
        geoProxy.loadRules();
        esriConfig.request.proxyUrl = this.appConfig.proxy.url;

        // configure assets path
        if (this.appConfig.assetsPath) {
            esriConfig.assetsPath = this.appConfig.assetsPath;
        }
        // configure fonts URL
        if (this.appConfig.fontsUrl) {
            esriConfig.fontsUrl = this.appConfig.fontsUrl;
        }
        // configure GeometryService URL
        if (this.appConfig.geometryServiceUrl) {
            esriConfig.geometryServiceUrl = this.appConfig.geometryServiceUrl;
        }
        // configure portal
        if (this.appConfig.portalUrl) {
            esriConfig.portalUrl = this.appConfig.portalUrl;

            const authentication = new Authentication({
                appConfig: this.appConfig,
                appNamespace: "naturgefahren-auftraege"
            });
            // remember authentication to get global access
            props.appStore.setAuthentication(authentication);

            authentication.login().then((credential: __esri.Credential) => {
                props.appStore.setLoggedIn(true, credential);
                this.auftragsverwaltung.loadApi();
            }).catch(() => {
                props.appStore.setLoggedIn(false);
            });

        } else {
            // eslint-disable-next-line no-console
            console.warn("No portal connection defined.");
        }

        this.mapStore.setAppConfig(this.appConfig);
        props.appStore.setTopic(
            {
                id: "auftragsverwaltung",
                name: this.mapViewerConfiguration.appName,
                label: this.mapViewerConfiguration.appName,
                routes: ["/"]
            } as TopicConfiguration
        );

        this.handleMapLoad = this.handleMapLoad.bind(this);
        this.handleNavigation = this.handleNavigation.bind(this);
        this.handleBrowserNavigation = this.handleBrowserNavigation.bind(this);
        this.handleResize = this.handleResize.bind(this);
    }

    /**
     * Extract route from url and find topic by rout
     * Set topic when route matches a topic
     */
    setTopicFromUrl(): void {
        if (this.props.params && this.props.params.subtopic) {
            const topicRoute = this.props.params.subtopic;
            // get topicId from url and set to mobx store
            const topic = this.mapViewerConfiguration.getTopicByRoute(`/${topicRoute}`);
            if(topic) {
                this.setTopic(topic);
            }
        }
    }

    setTopic(topic?: TopicConfiguration | null): void {
        const {appStore} = this.props;
        this.auftragsverwaltung.resetSelect();
        // cancel open edit sessions
        this.auftragsverwaltung.setEditing(false);

        if (topic){
            if (topic.isSubtopic) {
                // it's a subtopic
                appStore.setSubtopic(topic);
            } else {
                appStore.setTopic(topic);
            }
            // no direct navigation to selected objects possible by selecting topic
            // --> load list items, clear item selection
            appStore.clearObjectId();
            this.loadItemsForTopic(topic);
        }
        else {
            this.setTopiclessContent();
        }
    }

    /**
     * Content when no topic is selected
     */
    setTopiclessContent(): void {
        const {appStore} = this.props;
        // no topic
        appStore.setTopContent(null);
        appStore.setBottomContent(null);
        appStore.setPageTitle("Auftragsverwaltung Naturgefahren");
        // quick and dirty!
        if (typeof this.mapViewerConfiguration.htmlContent.mainPage === "string") {
            const topContent: React.ReactElement[] = [];
            if (this.appConfig.mode !== "production"
                && typeof this.mapViewerConfiguration.htmlContent.betaInfo === "string") {
                topContent.push(<div key={"test-banner"} className="flash">
                    <div className={"alert alert-error"}>
                        <p dangerouslySetInnerHTML={
                            {__html: this.mapViewerConfiguration.htmlContent.betaInfo}} />
                    </div>
                </div>);
            }
            topContent.push(
                <div key={"main-page"} dangerouslySetInnerHTML={
                    {__html: this.mapViewerConfiguration.htmlContent.mainPage}} className={"geo-top-content"}/>);
            appStore.setTopContent(topContent);
        }
        if (typeof this.mapViewerConfiguration.htmlContent.mainPage2 === "string") {
            const bottomContent = (
                <div dangerouslySetInnerHTML={{__html: this.mapViewerConfiguration.htmlContent.mainPage2}}/>
            );
            appStore.setBottomContent(bottomContent);
        }
        appStore.setBreadcrumbs(new NavigationConfiguration());
        appStore.setShowAside(true);
        appStore.setAsideContent(null);
        document.title = this.mapViewerConfiguration.appName;
        this.auftragsverwaltung.setShowMap(false);
    }

    /**
     * Extract station ID from url
     * Select station when station ID is present
     */
    selectObjectFromUrl(): void {
        const {appStore, params} = this.props;
        if (!params || !params.objectId) {
            return;
        }

        const {subtopic, objectId} = params;
        const oid = parseInt(objectId, 10);
        const user = appStore.esriCredential?.userId || "";

        // remember selected object ID. Only one selected object at the same time possible
        // TODO: selektiertes Objekt nur noch auf Auftragsverwaltung setzen. AppStore dazu nicht mehr verwenden
        appStore.setObjectId(objectId);

        // find matching topic to this route. set the topic
        const topic = appStore.setTopicByRoute(subtopic);
        if (!topic || !topic.id) {
            throw new Error("Route not found");
        }

        if (topic.id === "projectsGk" || topic.id === "projectsR") {
            if (objectId === "neu") {
                this.auftragsverwaltung.setShowMap(false);
                this.auftragsverwaltung.setEditing(true, "project");
                this.auftragsverwaltung.ProjectStore?.newItem(user).then((newProject) => {
                    if (newProject?.id) {
                        // do not show map when editing
                        this.auftragsverwaltung.setShowMap(false);
                        appStore.setObjectId(newProject.id.toString());
                        this.auftragsverwaltung.setEditing(true, "project");
                    }
                });
            } else if (objectId) {
                this.auftragsverwaltung.setShowMap(false);
                this.loadProjects().then(() => {
                    // find project and set as selected
                    const project = this.auftragsverwaltung.ProjectStore.getItem(objectId);
                    if (!project) {
                        appStore.showMessageBox({
                            title: "Projekt nicht gefunden",
                            message: `Kein Projekt mit der Projektnummer ${objectId} gefunden.`,
                            type: MessageBoxType.Warning
                        });
                    }
                    this.auftragsverwaltung.ProjectStore.selectedItem = project;
                });
            } else {
                appStore.clearObjectId();
                // initially load projects and contractors
                this.loadProjects()
            }
        }
        else if (topic.id === "contractors") {
            if (objectId === "neu") {
                this.auftragsverwaltung.setShowMap(false);
                this.auftragsverwaltung.ContractorStore.newItem(user).then((newContractor) => {
                    if (newContractor) {
                        // do not show map when editing
                        this.auftragsverwaltung.setShowMap(false);
                        appStore.setObjectId(newContractor.id.toString());
                        this.auftragsverwaltung.setEditing(true, "contractor");
                    }
                });
            } else if (objectId) {
                this.auftragsverwaltung.setShowMap(false);
                this.auftragsverwaltung.ContractorStore.loadItem(oid);
            } else {
                appStore.clearObjectId();
                this.auftragsverwaltung.ContractorStore.loadItems();
            }
        }
        else if (topic.id === "journal") {
            appStore.clearObjectId();
            this.auftragsverwaltung.JournalStore.loadItems();
        }
    }
    goToDefaultExtent(): void {
        this.mapStore.view?.goTo({
            target: new EsriGeometry(this.appConfig.mapView.center as __esri.GeometryProperties),
            zoom: this.zoom}).catch((error) => {
            if (error.name !== "view:goto-interrupted") {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        });
    }
    handleResize(): void {
        const uiHeight = this.appUiRef.current?.clientHeight;
        const uiWidth = this.appUiRef.current?.clientWidth;
        this.uiStore.setUiSize(uiWidth, uiHeight);
    }
    componentDidMount(): void {
        const {appStore} = this.props;
        document.title = this.mapViewerConfiguration.appName;
        // store route information to mobx store
        appStore.setRoute(this.props.location, this.props.params, this.props.navigate);
        this.setTopicFromUrl();
        this.selectObjectFromUrl();

        this.mapStore.setWebMap(this.mapViewerConfiguration.webMap);
        if (appStore.getRoute() === "/journal") {
            appStore.setShowAside(false);
        } else {
            appStore.setShowAside(true);
        }

        // rerun until navigate is available
        autorun((reaction: any) => {
            const location = appStore.location;
            if (!location) {
                return;
            }
            this.handleBrowserNavigation();
            reaction.dispose(); // stop autorun
        });
        autorun((reaction: any) => {
            const search = this.uiStore.widgets.get("search") as Search;
            if (!search || !search.state.viewModel) {
                return;
            }
            reaction.dispose(); // stop autorun
            this.prepareSearch(search);
        });
        autorun(() => {
            const search = this.uiStore.widgets.get("search-2") as Search;
            if (!search || !search.state.viewModel) {
                return;
            }
            // reaction.dispose(); // stop autorun
            this.prepareSearch(search);
        });
    }
    prepareSearch(search: Search): void {
        if (search.state.viewModel.hasEventListener("suggestionSelected")) {
            return;
        }
        search.state.viewModel.on( "suggestionSelected", () => {
            this.auftragsverwaltung.setShowMap(true);
        });
        search.state.viewModel.on( "resultFound", () => {
            // if (event.feature.attributes.egrid) {
            //     this.auftragsverwaltung.requestEinsatzplan(event.feature.attributes.egrid);
            // } else if (event.feature.attributes.bagenr) {
            //     this.auftragsverwaltung.requestFeuerwehr(event.feature.attributes.bagenr);
            // } else if (event.feature.attributes.egid) {
            //     let edid: number | undefined;
            //     if (event.feature.attributes.hasOwnProperty("edid")) {
            //         edid = parseInt(event.feature.attributes.edid, 10);
            //     }
            //     this.auftragsverwaltung.requestAuftrag(event.feature.attributes.egid, edid);
            // }
        });
        search.state.viewModel.on( "resultDetailFound", (event: any) => {
            // Resultat von Gebäudeversicherungsnummern anzeigen
            if (event.sourceConfig.categories.includes("gebaudeversicherungsnumer")) {
                if (event.response.features.length > 0) {
                    //const feature = event.response.features[0];
                    // if (feature.attributes.hasOwnProperty("gwr_egid")) {
                    //     this.auftragsverwaltung.requestAuftrag(feature.attributes.gwr_egid, undefined);
                    // }
                }
            }
        });
    }
    componentDidUpdate(prevProps: AppProps): void {
        if(prevProps.location.pathname !== this.props.location.pathname
            || (this.props.location.hash && prevProps.location.hash!==this.props.location.hash)){
            this.handleBrowserNavigation();
        }
    }
    componentWillUnmount(): void {
        // dispose reactions
        window.removeEventListener("resize", this.handleResize);
    }

    setSidebarVisible(visible: boolean): void {
        this.uiStore.setSidebarVisible(visible);
    }

    render(): React.ReactElement | null {
        let mapProperties: __esri.WebMapProperties | undefined;
        const messages: React.ReactElement[] = [];
        const {maintenance} = this.appConfig;
        const {sidebarVisible} = this.uiStore;
        const {status} = this.mapStore;
        const {appStore} = this.props;
        const {pageTitle, topContent, bottomContent, subtopic, tracker} = appStore;
        const {showMap} = this.auftragsverwaltung;
        const {northFacing} = this.state;
        const subtopicId = (subtopic? subtopic.id: "");
        if (this.mapStore.webMap){
            mapProperties = this.mapStore.webMap as __esri.WebMapProperties;
            if (this.mapViewerConfiguration.meta && Array.isArray(this.mapViewerConfiguration.meta.alerts)) {
                this.mapViewerConfiguration.meta.alerts.forEach((message, index) => {
                    // quick and dirty!
                    const alertContent = <p dangerouslySetInnerHTML={{__html: message.content}} />;
                    messages.push(
                        <MessageBlock
                            key={index}
                            index={index}
                            topic={"objekt.lu"}
                            header={message.title}
                            className={message.class? message.class: undefined}
                            content={alertContent}
                            appConfig={this.appConfig}/>
                    );
                });
            }
        }
        let newTopContent: React.ReactElement | null = null;
        let newBottomContent: React.ReactElement | null = null;
        if (pageTitle) {
            if (subtopicId === "") {
                document.title = this.mapViewerConfiguration.appName + " - Kanton Luzern";
                newTopContent = (
                    <Item.Group>
                        <Item>
                            <Item.Content>
                                {topContent}
                            </Item.Content>
                        </Item>
                    </Item.Group>
                );
            } else {
                document.title = pageTitle + " - " + this.mapViewerConfiguration.appName;
            }
        }
        let pageContent: React.ReactElement;
        if (subtopicId === "kontakt") {
            pageContent = (<Kontakt appStore={this.props.appStore}
                auftragsverwaltung={this.auftragsverwaltung}
                mapViewerConfig={this.mapViewerConfiguration}/>);
        } else if (subtopicId === "faq") {
            pageContent = (<Faq appStore={this.props.appStore}
                auftragsverwaltung={this.auftragsverwaltung}
                view={this.mapStore.view}
                mapViewerConfig={this.mapViewerConfiguration} />);
        } else if (subtopicId === "ueber") {
            pageContent = (<Ueber appStore={this.props.appStore}
                auftragsverwaltung={this.auftragsverwaltung}
                mapViewerConfig={this.mapViewerConfiguration} />);
        } else {
            pageContent = (
                <AuftragsverwaltungRouter
                    auftragsverwaltung={this.auftragsverwaltung}
                    view={this.mapStore.view}
                    appStore={this.props.appStore}
                    uiStore={this.uiStore}
                    mapStore={this.mapStore}
                    mapViewerConfig={this.mapViewerConfiguration}
                    appConfig={this.appConfig}
                    key={appStore.topic.id + subtopicId}
                />
            );
        }

        let addClass = "geo-app-body";
        addClass += (!showMap? " hide": "");
        addClass += (appStore.topic)? " topic-" + appStore.topic.id : "";
        addClass += (appStore.subtopic)? " subtopic-" + appStore.subtopic.id : "";
        addClass += " " + status;
        addClass += (northFacing? " geo-north-facing": " geo-not-north-facing");
        addClass += (this.uiStore.sidebarHigh)? " geo-sidebar-is_high" : "";
        // addClass += " "+this.state.addClass.join(" ");
        // addClass += isSmallDevice? " geo-small_device" : "";
        // addClass += (basemapOpacity <= 5)? " show-grid": "";
        // const cssProperties = { "--uiHeight": uiHeight + "px" };

        const mainContent: React.ReactElement = (
            <div className="mid-content-column">
                {(newTopContent)? newTopContent: topContent}
                <h2 id={"geo-page-title"}>{pageTitle}</h2>
                {this.auftragsverwaltung.status && this.auftragsverwaltung.status === "successful" &&
                    <Message attached="bottom" positive>
                        <Icon name={"data_floppy_disk"} />
                        Erfolgreich gespeichert!
                    </Message>
                }
                { this.auftragsverwaltung.showMap &&
                    <MapButtons
                        auftragsverwaltung={this.auftragsverwaltung}
                        appStore={appStore}
                        uiStore={this.uiStore}
                        view={this.mapStore.view}
                    />
                }
                <div id="map-root" >
                    <div className={addClass}>
                        <React.Suspense fallback={
                            <div className="pre-loader">
                                <div className="pre-loader-inner">Anwendung wird geladen ...
                                </div>
                            </div>
                        }>
                            <div className="pre-loader">
                                <div className="pre-loader-inner">Anwendung wird geladen ...</div>
                            </div>
                            <WebMap
                                mapProperties={mapProperties}
                                viewProperties={
                                    this.appConfig.mapView as __esri.MapViewProperties}
                                onLoad={this.handleMapLoad}
                                store={this.mapStore}
                                uiStore={this.uiStore}>
                                <LayerStack
                                    mapStore={this.mapStore}
                                    appStore={this.props.appStore}
                                    uiStore={this.uiStore}
                                    config={this.mapViewerConfiguration.layers}
                                />
                            </WebMap>
                        </React.Suspense>
                        <Ref innerRef={this.appUiRef}>
                            <div className="geo-app">
                                <Maintenance config={maintenance}/>
                                <div className="geo-ui-root">
                                    <div className="geo-ui">
                                        <div className="geo-ui-inner-container geo-ui-manual-container">
                                            <Popup config={this.mapViewerConfiguration.popup}
                                                mapViewerConfig={this.mapViewerConfiguration}
                                                appConfig={this.appConfig}
                                                view={this.mapStore.view}
                                                uiStore={this.uiStore}
                                                appStore={this.props.appStore}
                                                mapStore={this.mapStore}/>
                                        </div>
                                        <div className="geo-ui-inner-container geo-ui-corner-container">
                                            <div className="geo-ui-top-left geo-ui-corner">
                                                <Search
                                                    keyAppend={"2"}
                                                    mapStore={this.mapStore}
                                                    uiStore={this.uiStore}
                                                    config={this.appConfig.search}
                                                    tracker={tracker}/>
                                            </div>
                                            <div className="geo-ui-top-right geo-ui-corner"/>
                                            <div className="geo-ui-bottom-right geo-ui-corner">
                                                <BasemapGallery config={this.appConfig.basemapGallery}
                                                                view={this.mapStore.view}
                                                                uiStore={this.uiStore}
                                                                mapStore={this.mapStore} />
                                            </div>
                                            <div className="geo-ui-bottom-left geo-ui-corner">
                                                {messages.length > 0 && <div className="geo-messages">{messages}</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <Footer view={this.mapStore.view}
                                    appStore={this.props.appStore}
                                    appConfig={this.appConfig}
                                    uiStore={this.uiStore}
                                    mapStore={this.mapStore}/>
                            </div>
                        </Ref>
                    </div>
                </div>
                {pageContent}
                {(newBottomContent)? newBottomContent: bottomContent}
            </div>
        );
        return(
            <ErrorBoundary>
                <SemanticSidebar.Pushable>
                    <SemanticSidebar
                        animation="push"
                        onHide={() => this.setSidebarVisible(false)}
                        visible={sidebarVisible}
                    >
                        <MobileNavigation
                            config={this.mapViewerConfiguration.navigation}
                            appStore={this.props.appStore}
                            uiStore={this.uiStore}
                            handleNavigation={this.handleNavigation}/>
                    </SemanticSidebar>

                    <SemanticSidebar.Pusher>
                        <MessageBox store={this.props.appStore}/>
                        <div className="page">
                            <Header view={this.mapStore.view}
                                appConfig={this.appConfig}
                                appStore={this.props.appStore}
                                uiStore={this.uiStore}
                                mapStore={this.mapStore}
                                capabilities={[]} // add "search" to show search bar in header
                                searchConfig={this.appConfig.search}
                                mapViewerConfig={this.mapViewerConfiguration}
                            />
                            <div id="layout" className="wrapper container layout">
                                <Navigation config={this.mapViewerConfiguration.navigation}
                                    handleNavigation={this.handleNavigation}
                                    appConfig={this.appConfig}
                                    appStore={this.props.appStore}
                                    uiStore={this.uiStore}/>
                                <main className="layout__main">
                                    <Breadcrumbs config={this.mapViewerConfiguration.breadcrumbs}
                                        handleNavigation={this.handleBreadcrumbNavigation}
                                        uiStore={this.uiStore}
                                        appStore={this.props.appStore}
                                        appConfig={this.appConfig}/>
                                    <div className="content one-col">
                                        {mainContent}
                                    </div>
                                </main>
                                <Aside
                                    view={this.mapStore.view}
                                    appConfig={this.appConfig}
                                    appStore={this.props.appStore}
                                    uiStore={this.uiStore}
                                    mapStore={this.mapStore}
                                    mapViewerConfig={this.mapViewerConfiguration}
                                    auftragsverwaltung={this.auftragsverwaltung}
                                />
                            </div>
                            <AuftragsverwaltungFooter view={this.mapStore.view}
                                appStore={this.props.appStore}
                                appConfig={this.appConfig}
                                uiStore={this.uiStore}
                                mapStore={this.mapStore}/>
                        </div>
                    </SemanticSidebar.Pusher>
                </SemanticSidebar.Pushable>
                <CookieBanner mapViewerConfig={this.mapViewerConfiguration}/>
            </ErrorBoundary>
        );
    }

    handleMapLoad(map: __esri.Map, view: __esri.MapView | __esri.SceneView): void {
        // TODO: AppState status erst auf complete schalten, wenn alle Widgets geladen sind
        // this.appStore.setState({ status: "complete" });
        if (this.mapViewerConfiguration.locateButtonEnabled) {
            const locateWidget = new EsriLocate({
                view
            });
            view.ui.add(locateWidget, "top-right");
        }
        // mode zoom buttons to top right
        view.ui.move("zoom", "top-right");
        const compassWidget = new EsriCompass({
            view
        });
        view.ui.add(compassWidget, "top-right");
        view.watch("rotation", (newValue: number)=>{
            this.setState({northFacing: newValue === 0});
        });

        view.popupEnabled = false;
    }
    handleBreadcrumbNavigation(route: string): void {
        const topic = this.mapViewerConfiguration.getTopicByRoute("/" + route.split("/")[1]);
        if (!topic) {
            throw new Error("Route not found");
        }
        this.handleNavigation(route, topic.id);
    }
    handleNavigation(route: string, topicId: string): void {
        const {appStore} = this.props;
        // remove basename
        if (this.appConfig.basename.length > 1) {
            route = route.substring(this.appConfig.basename.length - 1);
        }
        appStore.setRoutePath(route);
        const topic = this.mapViewerConfiguration.getTopic(topicId);
        this.auftragsverwaltung.resetSelect();
        this.auftragsverwaltung.resetStatus();
        appStore.clearObjectId();
        // reset search term
        this.auftragsverwaltung.ProjectStore.searchTerm = "";

        if (topic?.id === "home") {
            this.setTopic();
            this.uiStore?.setPopupVisible(false);
            this.goToDefaultExtent();
        }
        else if (topic?.id === "map") {
            this.setTopic(topic);
            this.auftragsverwaltung.setShowMap(true);
            this.uiStore?.setPopupVisible(false);
        } else {
            this.setTopic(topic);
        }
    }
    /**
     * Register listener to set topic, when navigation is done with browser back/forward buttons
     */
    handleBrowserNavigation(): void {
        const { appStore, location, params } = this.props;
        // store route information to mobx store
        appStore.setRoute(location, params, this.props.navigate);
        this.handleSubtopicBrowserNavigation();
    }
    handleSubtopicBrowserNavigation(): void {
        const { appStore, params } = this.props;
        // if(!params.subtopic) {
        //     this.auftragsverwaltung.resetSelect();
        //     appStore.setSubtopic();
        //     this.setTopic();
        // }else if(!appStore.subtopic || !appStore.subtopic.routes.includes("/" + params.subtopic)){
        //     const subtopic = this.mapViewerConfiguration.getTopicByRoute(
        //         "/" + params.subtopic, this.mapViewerConfiguration.viewManager?.views as TopicConfiguration[]
        //     );
        //     if(subtopic) {
        //         appStore.setSubtopic(subtopic);
        //     }
        // }
        if(params.objectId !== appStore.objectId){
            appStore.setObjectId(this.props.params.objectId);
        }
        // reset selected object when no objectId when using back/forward browser
        if (!params.objectId) {
            this.auftragsverwaltung.resetSelect();
            this.auftragsverwaltung.setEditing(false);
        }
    }

    /**
     * Load items relevant for this topic
     *
     * @param topic
     * @private
     */
    private loadItemsForTopic(topic: TopicConfiguration): void {
        if (topic.id === "projectsGk" || topic.id === "projectsR") {
            // initially load projects and contractors
            this.loadProjects();
        }
        else if (topic.id === "contractors") {
            this.auftragsverwaltung.ContractorStore.loadItems();
        }
        else if (topic.id === "journal") {
            this.auftragsverwaltung.JournalStore.loadItems();
        }
    }

    private loadProjects(): Promise<void> {
        return new Promise((resolve) => {
            // first get contractors and municipalities with full information
            const preloaderPromises = [
                this.auftragsverwaltung.ContractorStore.loadItems(),
                this.auftragsverwaltung.MunicipalityStore.loadItems()
            ];
            Promise.all(preloaderPromises).then((values) => {
                // @ts-expect-error first value is contractors
                const contractors: Contractor[] = values[0];
                // @ts-expect-error second value is municipalities
                const municipalities: Municipality[] = values[1];

                const sortedMunicipalities = municipalities.sort(Municipality.sortByName);

                // now load projects and related municipalities
                this.auftragsverwaltung.ProjectStore?.loadItems().then((projects) => {
                    // @ts-expect-error null values are filtered!
                    const projectIds: number[] = projects.map((p: Project) => p.id).filter((id) => id);

                    this.auftragsverwaltung.ProjectMunicipalityStore.loadItems(projectIds).then((pms) => {

                        projects.forEach((p) => {
                            // associate municipality objects to energy plannings
                            p.municipalities = sortedMunicipalities.filter((m: Municipality) => {
                                return pms.some((pm) => {
                                    return pm.bfsNr === m.bfsNr && pm.projectId === p.id;
                                });
                            });
                            if (p.contractorId) {
                                p.contractor = contractors.find((c) => c.id === p.contractorId);
                            }
                        });
                        resolve();
                    });

                });
            });
        });
    }
}
